// components/consumers/requests/RequestsTab.tsx

import React, { useState, useMemo } from 'react';
import { Button } from "@shared/ui/button";
import { ServiceRequest } from '@features/cx/consumerManagement/types';
import { useServiceRequests } from '@features/cx/consumerManagement/hooks';
import { GenericTable } from '@shared/components/GenericTable';
import { SortConfig, TableAction, TableColumn } from '@shared/types/table';
import { PAGE_SIZE } from '@shared/utils/constants';
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ca } from 'zod/dist/types/v4/locales';

interface RequestsTabProps {
  consumerId: string;
  remoteUtilityId: number;
}

export const RequestsTab: React.FC<RequestsTabProps> = ({ consumerId, remoteUtilityId }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useServiceRequests(consumerId, {
    page: currentPage,
    limit: PAGE_SIZE,
    query: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    remote_utility_id: remoteUtilityId,
  });
  const navigate = useNavigate();
  // Flatten the paginated response data
  const flattenedServiceRequests = useMemo(() => {
    if (!data?.results) return [];
    return data.results;
  }, [data]);

  // Define table columns
  const columns: TableColumn<ServiceRequest>[] = [
    {
      key: 'requestNo',
      header: 'Request ID',
      sortable: true,
    },
    {
      key: 'requestTypeDisplay',
      header: 'Type',
      sortable: true,
    },
    {
      key: 'statusDisplay',
      header: 'Status',
      sortable: true,
      type: 'badge',
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case 'COMPLETED':
              return 'default';
            case 'PENDING':
              return 'secondary';
            case 'CREATED':
              return 'outline';
            case 'CANCELLED':
              return 'destructive';
            default:
              return 'default';
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case 'COMPLETED':
              return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200';
            case 'CREATED':
              return 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200';
            case 'PENDING':
              return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200';
            case 'CANCELLED':
              return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200';
            default:
              return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
          }
        }
      }
    },
    {
      key: 'createdDate',
      header: 'Created On',
      sortable: true,
    },
    {
      key: 'createdUserRemoteName',
      header: 'Created By',
      sortable: true,
    },
  ];

  const handleNavigation = (row: any) => {
  const { requestType, id, statusDisplay } = row;

  const isCompleted = statusDisplay?.toUpperCase() === "COMPLETED";
  const section = isCompleted ? "history" : "current";

  switch (requestType) {
    case "Transfer":
      navigate(`/cx/transfer/${section}/${id}`);
      break;
    case "Disconnect Permanent":
      navigate(`/cx/disconnections/${section}/${id}`);
      break;
    case "Reconnection":
      navigate(`/cx/reconnections/${section}/${id}`);
      break;
    default:
      return null;
  }
};

  const actions: TableAction<any>[] = [
      {
            label: "View",
            icon: <Eye className="h-4 w-4" />,
            onClick: (row: any) => {handleNavigation(row)},
          },
    ];
  // Handle sorting
  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleSelectionChange = (newSelectedRows: string[]) => {
    setSelectedRows(newSelectedRows);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <Button 
          onClick={() => {
            setCurrentPage(1);
          }}
          className="mt-2"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={flattenedServiceRequests}
        columns={columns}
        actions={actions}
        actionsType='icons'
        rowKey="id"
        pagination={{
          currentPage: data?.page || 1,
          totalPages: data?.total_pages || 1,
          totalItems: data?.count || 0,
          itemsPerPage: data?.limit || 10,
          startItem: data?.page ? (data.page - 1) * (data.limit || 10) + 1 : 0,
          endItem: data?.page ? Math.min(data.page * (data.limit || 10), data.count || 0) : 0,
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search service requests..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No service requests found"
        getRowClassName={(row, index) => {
          return row.status === 1 ? 'opacity-60' : '';
        }}
      />
    </div>
  );
};